import React, { useState } from "react";
import { 
  ArrowRight, 
  Download, 
  Users, 
  BookOpen, 
  Check, 
  Sparkles, 
  ShieldCheck,
  ShieldAlert
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { validateBusinessEmail } from "../../../utils/emailValidation";

interface GovernanceKitFormProps {
  email: string;
  setEmail: (email: string) => void;
  emailError: string | null;
  setEmailError: (error: string | null) => void;
  handleDownloadSubmit: (e: React.FormEvent) => void;
}

const GovernanceKitForm: React.FC<GovernanceKitFormProps> = ({
  email,
  setEmail,
  emailError,
  setEmailError,
  handleDownloadSubmit
}) => (
  <form onSubmit={handleDownloadSubmit} className="space-y-3">
    <div className="text-[10px] font-mono text-brand-sand uppercase tracking-wider font-bold">
      Verify Corporate Email to Secure Transfer
    </div>
    <div className="flex gap-2">
      <input
        type="email"
        required
        placeholder="executive@company.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (emailError) setEmailError(null);
        }}
        className={`flex-1 bg-stone-955 border rounded-sm px-3 py-2 text-xs text-white focus:outline-none font-mono transition-all ${
          emailError
            ? "border-red-500 focus:border-red-500 bg-red-950/20"
            : "border-brand-sand/30 focus:border-brand-orange"
        }`}
      />
      <button
        type="submit"
        className="bg-brand-orange hover:bg-brand-burgundy text-white text-xs font-mono font-bold px-4 py-2 rounded-sm cursor-pointer transition-colors"
      >
        Access
      </button>
    </div>
    {emailError && (
      <div className="text-[9px] text-red-400 font-mono flex items-start gap-1.5 leading-normal">
        <ShieldAlert className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
        <span>{emailError}</span>
      </div>
    )}
    <p className="text-[9px] text-stone-500 font-light leading-normal">
      By requesting transmission, you agree to secure cryptographic delivery under non-commercial practice license guidelines.
    </p>
  </form>
);

const GovernanceKitSuccess: React.FC<{ email: string }> = ({ email }) => (
  <motion.div
    initial={{ scale: 0.95, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className="text-center py-2 space-y-1"
  >
    <ShieldCheck className="w-6 h-6 text-brand-orange mx-auto mb-1" />
    <div className="text-xs font-bold text-white font-mono">ENVELOPE PREPARED</div>
    <p className="text-[10px] text-stone-400 font-light leading-normal">
      Secure download packet has been sent to <strong>{email}</strong> under cryptographic validation.
    </p>
  </motion.div>
);

export default function SelfSelectionCTA() {
  const [downloadRequested, setDownloadRequested] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Smooth scroll to InvestmentSection pricing calculator
  const scrollToInvestment = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const target = document.querySelector("#investment");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleDownloadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const valResult = validateBusinessEmail(email);
    if (!valResult.isValid) {
      setEmailError(valResult.error || "Please enter a valid business email.");
      return;
    }
    setEmailError(null);
    setSubmitSuccess(true);
    setTimeout(() => {
      setDownloadRequested(false);
      setSubmitSuccess(false);
      setEmail("");
    }, 4000);
  };

  return (
    <section id="self-selection" className="py-16 bg-brand-charcoal border-y border-brand-sand/15 relative overflow-hidden">
      {/* Visual background accents */}
      <div className="absolute inset-0 bg-radial-gradient from-brand-sand/5 to-transparent pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] bg-brand-burgundy/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
          <p className="text-xs font-mono uppercase tracking-widest text-brand-orange font-bold">
            ✦ Practice Gateway & Qualification
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-display">
            Start at the Right Level of Commitment
          </h2>
          <p className="text-sm md:text-base text-stone-400 font-light leading-relaxed max-w-2xl mx-auto">
            If you have executive sponsorship, budget authority, and real operational risk, begin with advisory intake. If you are still preparing internally, use the open governance resources first.
          </p>
          <p className="text-xs text-stone-500 font-light pt-2">
            Engagements and resources can support ISO/IEC 42001-aligned AI governance, audit evidence, lifecycle controls, and executive accountability.
          </p>
        </div>

        {/* Double-Path Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
          
          {/* PATH 1: DIRECT ADVISORY (HIGH TOUCH) */}
          <div className="border border-brand-orange/30 bg-stone-900/60 hover:border-brand-orange/60 rounded-sm p-6 sm:p-8 flex flex-col justify-between transition-all duration-300 relative group shadow-lg shadow-brand-orange/5">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-orange to-brand-amber opacity-100 transition-opacity" />
            
            <div className="space-y-6">
              {/* Badge & Icon */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-white font-bold uppercase tracking-wider bg-brand-burgundy px-2.5 py-1 rounded-sm">
                    LIMITED RETAINER CAPACITY
                  </span>
                </div>
                <Users className="w-5 h-5 text-brand-orange" />
              </div>

              {/* Header */}
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-white font-display">
                  Fractional CAIO & Direct Advisory
                </h3>
                <p className="text-xs text-stone-400 font-light leading-relaxed">
                  For leadership teams seeking direct strategic involvement, architecture review, vendor control, and governed AI implementation oversight.
                </p>
              </div>

              {/* Parameter Metrics */}
              <div className="pt-4 border-t border-brand-sand/10 space-y-3 text-xs text-stone-300 font-light">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0 mt-1.5" />
                  <span className="leading-relaxed"><strong>Minimum engagement:</strong> $25,000/month fractional retainer</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0 mt-1.5" />
                  <span className="leading-relaxed"><strong>Best fit:</strong> executive sponsor, internal implementation owner, real operating risk</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0 mt-1.5" />
                  <span className="leading-relaxed"><strong>Output:</strong> board-ready roadmap, architecture decisions, vendor control, and implementation oversight</span>
                </div>
              </div>
            </div>

            {/* Action Call */}
            <div className="pt-8">
              <button
                onClick={scrollToInvestment}
                className="w-full bg-brand-burgundy hover:bg-brand-orange text-white font-mono font-bold uppercase tracking-wider text-xs py-3.5 rounded-sm transition-colors text-center cursor-pointer flex items-center justify-center gap-1.5 group-hover:translate-y-[-2px] transition-transform duration-200"
              >
                Initiate Advisory Intake <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* PATH 2: KNOWLEDGE VAULT (SELF-SERVICE / PREPARATION) */}
          <div className="border border-stone-800 bg-stone-900/20 hover:border-stone-700 rounded-sm p-6 sm:p-8 flex flex-col justify-between transition-all duration-300 relative group">
            <div className="absolute top-0 left-0 w-full h-1 bg-stone-800 opacity-100 group-hover:bg-stone-700 transition-colors" />
            
            <div className="space-y-6">
              {/* Badge & Icon */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-stone-400 font-bold uppercase tracking-wider bg-white/5 px-2.5 py-1 rounded-sm">
                  SELF-DIRECTED RESOURCES
                </span>
                <BookOpen className="w-5 h-5 text-stone-400" />
              </div>

              {/* Header */}
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-stone-200 font-display">
                  Self-Directed AI Governance Kit
                </h3>
                <p className="text-xs text-stone-400 font-light leading-relaxed">
                  For teams still preparing internally: open governance templates, zero-trust schema ideas, and cost-control checklists you can review before committing to advisory work.
                </p>
              </div>

              {/* Parameter Metrics */}
              <div className="pt-4 border-t border-stone-800/50 space-y-3 text-xs text-stone-300 font-light">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-600 shrink-0 mt-1.5" />
                  <span className="leading-relaxed"><strong>Cost:</strong> $0 open-source</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-600 shrink-0 mt-1.5" />
                  <span className="leading-relaxed"><strong>Includes:</strong> zero-trust schema rules and cost-control checklist</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-stone-600 shrink-0 mt-1.5" />
                  <span className="leading-relaxed"><strong>License:</strong> MIT, royalty-free corporate use</span>
                </div>
              </div>
            </div>

            {/* Action Call / Expand Form */}
            <div className="pt-8">
              <AnimatePresence mode="wait">
                {!downloadRequested ? (
                  <motion.button
                    key="trigger"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setDownloadRequested(true)}
                    className="w-full bg-stone-800 hover:bg-stone-700 text-white font-mono font-bold uppercase tracking-wider text-xs py-3.5 rounded-sm transition-colors text-center cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    Download Governance Kit <Download className="w-4 h-4" />
                  </motion.button>
                ) : (
                  <motion.div
                    key="form-container"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="w-full bg-stone-900 border border-brand-sand/20 p-4 rounded-sm"
                  >
                    {!submitSuccess ? (
                      <GovernanceKitForm
                        email={email}
                        setEmail={setEmail}
                        emailError={emailError}
                        setEmailError={setEmailError}
                        handleDownloadSubmit={handleDownloadSubmit}
                      />
                    ) : (
                      <GovernanceKitSuccess email={email} />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
